import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaArrowLeft, FaArrowRight, FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import '../styles/recentWorks.css';

const works = [
  {
    title: "QR-code-Encrypt_Decrypt",
    description: "This Work contains the source code for an Encryption and Decryption application built using the Python Flask framework and Angular 7. It provides a user-friendly interface for Generating an Encrypted QR code using the blowfish algorithm and decrypting the QR code with a Key. The app features end-to-end encryption for privacy.",
    imageUrl: "/qr.jpg",
    tech: ["Python", "Flask", "Angular 7", "HTML/CSS", "SQL"],
    period: "2024",
    links: {
      live: "",
      github: "https://github.com/amal-alex07/QR-code-Encrypt_Decrypt"
    }
  },
  {
    title: "Shop 24",
    description: "The Shop 24 project is an e-commerce platform designed to provide customers with unparalleled convenience and access to a wide range of products and services around the clock. In today's fast-paced world, consumers demand the flexibility to shop whenever it suits them, and this project aims to meet that need.",
    imageUrl: "/shop.jpg",
    tech: ["Python", "Django"],
    period: "2024",
    links: {
      live: "",
      github: "https://github.com/amal-alex07/Shop24"
    }
  },
  {
    title: "Tic-Tac-Toe Game",
    description: " the Tic-Tac-Toe Game built with React! This is a simple implementation of the classic Tic-Tac-Toe game where two players (X and O) take turns to fill a 3x3 grid. The game ends when one player achieves three marks in a row (horizontally, vertically, or diagonally) or when there is a tie.",
    imageUrl: "/tic.jpg",
    tech: ["React", "Node.js", "Game"],
    period: "2024",
    links: {
      live: "",
      github: "https://github.com/amal-alex07/Tic-Tac-Toe/tree/main/tic-tac-toe"
    }
  },
   {
    title: "File upload vulnerabilities",
    description: " This project provides an overview of file upload vulnerabilities in web applications and offers guidance on identifying and mitigating these risks.",
    imageUrl: "/file.png",
    tech: ["Security", "Exploiting ", "VULNERABILITY"],
    period: "2024",
    links: {
      live: "",
      github: "https://github.com/amal-alex07/File-upload-VULNERABILITY-"
    }
  }
];

const RecentWorks = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % works.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.8
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.8
    })
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection) => {
    setDirection(newDirection);
    setCurrentIndex((prev) => (prev + newDirection + works.length) % works.length);
  };

  return (
    <div className="recent-works-page">
      <div className="dot-pattern"></div>
      <div ref={ref} className="recent-works-section">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="section-title"
        >
          Recent Works
        </motion.h2>

        <div className="slideshow-container">
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
                scale: { duration: 0.3 }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              className="slide"
            >
              <div className="slide-content">
                <div className="slide-image">
                  <img 
                    src={works[currentIndex].imageUrl} 
                    alt={works[currentIndex].title}
                  />
                </div>
                <div className="slide-info">
                  <h3>{works[currentIndex].title}</h3>
                  <p>{works[currentIndex].description}</p>
                  <div className="tech-stack">
                    {works[currentIndex].tech.map((tech, index) => (
                      <span key={index} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                  <div className="project-links">
                    <a href={works[currentIndex].links.github} target="_blank" rel="noopener noreferrer">
                      <FaGithub /> View Code
                    </a>
                    <a href={works[currentIndex].links.live} target="_blank" rel="noopener noreferrer">
                      <FaExternalLinkAlt /> Live Demo
                    </a>
                  </div>
                  <span className="period">{works[currentIndex].period}</span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <button className="nav-button prev" onClick={() => paginate(-1)}>
            <FaArrowLeft />
          </button>
          <button className="nav-button next" onClick={() => paginate(1)}>
            <FaArrowRight />
          </button>

          <div className="slide-dots">
            {works.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentWorks; 